<template>
  <div
    class="video-player-page"
    :class="{
      'recording-active': recordingPhase === 'active',
      'cursor-hidden': recordingPhase === 'active'
    }"
    ref="root"
  >
    <div class="stage" ref="stage">
      <!-- stage-inner's layout box == visual box (actual rendered size).
           This is the Region-Capture target. Its child stage-content is
           fixed 1920x1080 and scaled down to fit. -->
      <div class="stage-inner" ref="stageInner" :style="stageOuterStyle">
        <div class="stage-content" :style="stageContentStyle">
          <!-- Bundle mode: one persistent iframe, slides switched via postMessage. -->
          <iframe
            v-if="slidesBundleUrl"
            ref="bundleIframe"
            :src="slidesBundleUrl"
            class="bundle-iframe"
            sandbox="allow-scripts allow-same-origin"
            referrerpolicy="no-referrer"
            @load="onBundleLoad"
          ></iframe>
          <!-- Legacy per-slide mode: transition between per-cue renderers. -->
          <transition v-else :name="transitionName" mode="out-in">
            <div
              class="slide-wrapper"
              :key="currentIndex"
            >
              <SlideRenderer
                v-if="currentSlide"
                :slide="currentSlide"
                :config="script.video_config"
                :progress="slideProgress"
              />
            </div>
          </transition>
          <!-- Caption overlay (lives inside the 1920x1080 stage content). -->
          <div
            v-if="showCaptions && currentSubtitle && recordingPhase !== 'arming'"
            class="caption-layer"
          >
            <div class="caption-bar">{{ currentSubtitle }}</div>
          </div>
        </div>
      </div>

      <!-- Countdown overlay -->
      <div v-if="recordingPhase === 'arming'" class="countdown-overlay">
        <div class="countdown-label">Get ready</div>
        <div class="countdown-number" :key="countdownValue">{{ countdownValue }}</div>
        <div class="countdown-hint">Any movement or key cancels · Recording starts in {{ countdownValue }}s</div>
      </div>

      <!-- ESC confirmation toast -->
      <div v-if="recordingPhase === 'active' && escConfirmPending" class="esc-confirm">
        Press <kbd>Esc</kbd> again to quit recording
      </div>

      <!-- Abort reason toast (diagnostic) -->
      <div v-if="lastAbortReason" class="abort-toast">
        <div class="abort-toast-title">Countdown aborted by:</div>
        <div class="abort-toast-body">{{ lastAbortReason }}</div>
      </div>

      <!-- Recording indicator — outside the 16:9 stage-inner so it isn't in
           the recorded frame if capture is constrained to the inner content;
           for tab capture, it's in the letterbox. Either way, unobtrusive. -->
      <div v-if="recordingPhase === 'active' && recordingToFile" class="rec-badge">
        <span class="rec-dot"></span> REC
      </div>
    </div>

    <!-- Auto-load banner (when opened with ?collection_id=…) -->
    <div v-if="autoLoadedCollection" class="autoload-banner">
      <span class="autoload-dot"></span>
      <span class="autoload-text">
        Loaded from collection: <strong>{{ autoLoadedCollection.title }}</strong>
        <span class="autoload-meta">· status: {{ autoLoadedCollection.status }}</span>
      </span>
      <a v-if="autoLoadedCollection.youtube_url" :href="autoLoadedCollection.youtube_url" target="_blank" rel="noopener" class="autoload-link">view on YouTube ↗</a>
    </div>
    <div v-if="autoLoadError" class="autoload-error">
      Auto-load failed: {{ autoLoadError }}
    </div>

    <!-- Post-recording toasts, outside the stage so they never appear in a
         recorded video. -->
    <div v-if="recordingSavedToast" class="saved-toast">
      <div>
        <strong>Recording saved</strong>
        <span style="opacity:0.7"> · {{ lastRecordingName }}</span>
      </div>
      <a :href="lastRecordingUrl" :download="lastRecordingName" class="btn btn-sm">Download again</a>
    </div>
    <div v-if="recordingError" class="error-toast">
      {{ recordingError }}
    </div>

    <audio ref="audio" preload="auto" @ended="onAudioEnded" @loadedmetadata="onAudioMeta"></audio>

    <div class="controls" v-show="recordingPhase !== 'active'">
      <div class="progress">
        <div class="bar" :style="{ width: overallProgress * 100 + '%' }"></div>
      </div>
      <div class="row">
        <button class="btn" @click="restart" title="Restart">⟲</button>
        <button class="btn" @click="prevSlide" :disabled="currentIndex === 0" title="Previous">‹</button>
        <button class="btn primary" @click="togglePlay" :disabled="recordingPhase === 'arming'">
          <span v-if="!playing">▶ Play</span>
          <span v-else>❚❚ Pause</span>
        </button>
        <button class="btn" @click="nextSlide" :disabled="currentIndex >= slides.length - 1" title="Next">›</button>
        <button class="btn record" @click="startRecordFlow" :disabled="recordingPhase === 'arming'" title="Start recording">
          ● Record
        </button>
        <span class="slide-count">Slide {{ currentIndex + 1 }} / {{ slides.length }}</span>
        <span class="spacer"></span>
        <label class="toggle" title="Show captions over slides">
          <input type="checkbox" v-model="showCaptions" />
          Captions
        </label>
        <button class="btn" @click="editorOpen = !editorOpen">
          {{ editorOpen ? 'Hide editor' : '🗂 Edit timeline' + (cues.length ? ` (${cues.length} cues)` : '') }}
        </button>
      </div>
      <div class="hint">
        ● Record = browser screen capture → fullscreen → 4s countdown → auto-play → <code>.webm</code> downloads to your Downloads folder on stop. Pick <em>this tab</em> in the picker. Double-tap <kbd>Esc</kbd> to quit early.
        Mode: <strong>{{ masterMode ? 'Master audio + cues' : (cues.length ? 'Cues only (no audio, fallback durations)' : 'Built-in sample') }}</strong>
      </div>

      <!-- Master audio + slides bundle + cue list editor -->
      <div v-if="editorOpen" class="editor">
        <!-- 1. Master audio -->
        <div class="editor-section">
          <div class="section-head">
            <strong>1. Master audio</strong>
            <span class="section-note">One continuous narration. Primary source of timing.</span>
          </div>
          <div class="master-row">
            <input type="file" accept="audio/*" @change="onMasterAudioFile" />
            <div v-if="masterAudioName" class="file-status">
              ✓ {{ masterAudioName }}
              <span v-if="masterAudioDurationMs"> · {{ (masterAudioDurationMs / 1000).toFixed(1) }}s total</span>
              <button class="btn btn-sm danger" style="margin-left:8px" @click="removeMasterAudio">Remove</button>
            </div>
            <div v-else class="file-status muted">No master audio — fallback to fixed per-slide duration.</div>
          </div>
        </div>

        <!-- 2. Slides bundle -->
        <div class="editor-section">
          <div class="section-head">
            <strong>2. Slides bundle</strong>
            <span class="section-note">
              One HTML file containing every slide as <code>&lt;section data-slide-id="..."&gt;</code>.
              Switched in-place via <code>postMessage</code> — no reload, no flicker.
            </span>
          </div>
          <div class="master-row">
            <input type="file" accept=".html,.htm,text/html" @change="onSlidesBundleFile" />
            <div v-if="slidesBundleName" class="file-status">
              ✓ {{ slidesBundleName }}
              <span v-if="detectedSlideIds.length"> · {{ detectedSlideIds.length }} slides detected</span>
              <button class="btn btn-sm danger" style="margin-left:8px" @click="removeSlidesBundle">Remove</button>
            </div>
            <div v-else class="file-status muted">No bundle — falls back to per-cue HTML upload below.</div>
          </div>
          <div v-if="detectedSlideIds.length" class="detected-ids">
            Found: <code v-for="(id, i) in detectedSlideIds" :key="id">{{ id }}<span v-if="i < detectedSlideIds.length - 1">, </span></code>
          </div>
        </div>

        <!-- 3. Cue list -->
        <div class="editor-section">
          <div class="section-head">
            <strong>3. Cue list</strong>
            <span class="section-note">
              At each <code>at_ms</code>, switch to <code>slide_id</code>.
            </span>
            <span class="spacer"></span>
            <button class="btn btn-sm" @click="addCue()">+ Add cue</button>
            <button class="btn btn-sm" @click="clearCues" :disabled="!cues.length">Clear cues</button>
          </div>

          <details class="import-details">
            <summary>Import / export JSON</summary>
            <div class="import-body">
              <div class="import-actions">
                <button class="btn btn-sm" @click="triggerCueJsonUpload">Upload JSON file</button>
                <input
                  ref="cueJsonInput"
                  type="file"
                  accept=".json,application/json"
                  @change="onCueJsonFile"
                  style="display:none"
                />
                <button class="btn btn-sm" @click="applyCueImport" :disabled="!cueImportText.trim()">Apply JSON → cues</button>
                <button class="btn btn-sm" @click="exportCueJson" :disabled="!cues.length">Export current cues</button>
                <span class="import-hint">
                  or paste below · v1.1: <code>{ "slides_bundle": "slides.html", "slides": [{ "id": "s1", "at_ms": 0 }, ...] }</code>
                </span>
              </div>
              <textarea
                class="json-input"
                rows="6"
                v-model="cueImportText"
                :placeholder='sampleJsonPlaceholder'
              ></textarea>
              <div v-if="cueImportError" class="import-error">{{ cueImportError }}</div>
            </div>
          </details>

          <div class="editor-list">
            <div v-for="(cue, idx) in cues" :key="cue.id" class="slide-row">
              <div class="slide-row-head">
                <strong>Cue {{ idx + 1 }}</strong>
                <span class="time-badge">{{ formatMs(cue.atMs) }}</span>
                <span v-if="cue.slideId" class="time-badge subtle">
                  {{ cue.slideId }}<span v-if="cue.step"> · step {{ cue.step }}</span>
                </span>
                <span v-if="cueEndMs(idx) !== null" class="time-badge subtle">
                  → {{ formatMs(cueEndMs(idx)) }}
                  · {{ ((cueEndMs(idx) - cue.atMs) / 1000).toFixed(1) }}s
                </span>
                <span class="spacer"></span>
                <button class="btn btn-sm" @click="moveCue(idx, -1)" :disabled="idx === 0">↑</button>
                <button class="btn btn-sm" @click="moveCue(idx, 1)" :disabled="idx === cues.length - 1">↓</button>
                <button class="btn btn-sm danger" @click="removeCue(idx)">Remove</button>
              </div>
              <div class="slide-row-body">
                <div class="field">
                  <label>at (seconds)</label>
                  <input
                    type="number"
                    class="num wider"
                    min="0"
                    step="0.1"
                    :value="(cue.atMs / 1000).toFixed(1)"
                    @change="updateCueAtMs(idx, $event.target.value); sortCues()"
                  />
                </div>
                <div class="field" v-if="slidesBundleUrl">
                  <label>Slide ID · Step</label>
                  <div class="field-inline">
                    <input
                      type="text"
                      class="slide-id-input"
                      :value="cue.slideId"
                      @input="updateCueSlideId(idx, $event.target.value)"
                      :placeholder="detectedSlideIds[0] || 's1'"
                      :list="'slide-ids-list'"
                    />
                    <input
                      type="number"
                      class="num step-input"
                      min="0"
                      step="1"
                      :value="cue.step || 0"
                      @input="updateCueStep(idx, $event.target.value)"
                      title="Step within the slide (0 = initial state, 1+ = reveal/highlight steps)"
                    />
                  </div>
                  <datalist id="slide-ids-list">
                    <option v-for="id in detectedSlideIds" :key="id" :value="id" />
                  </datalist>
                  <div class="file-status" :class="{ muted: !cue.slideId || !detectedSlideIds.includes(cue.slideId) }">
                    <span v-if="cue.slideId && detectedSlideIds.includes(cue.slideId)">✓ in bundle · step {{ cue.step || 0 }}</span>
                    <span v-else-if="cue.slideId">⚠ not found in bundle</span>
                    <span v-else>No ID set</span>
                  </div>
                </div>
                <div class="field" v-else>
                  <label>HTML slide (legacy, per-cue)</label>
                  <input type="file" accept=".html,.htm,text/html" @change="onCueHtmlFile($event, idx)" />
                  <div class="file-status" v-if="cue.htmlName">✓ {{ cue.htmlName }}</div>
                  <div class="file-status muted" v-else>No file · upload a bundle above or attach one here</div>
                </div>
                <div class="field wide">
                  <label>Subtitle / caption text</label>
                  <textarea
                    rows="2"
                    :value="cue.subtitle"
                    @input="updateCueSubtitle(idx, $event.target.value)"
                    placeholder="Shown as a caption bar during this cue. Blank = no caption."
                  ></textarea>
                </div>
              </div>
            </div>
            <div v-if="!cues.length" class="editor-empty">
              No cues yet. Click <strong>+ Add cue</strong>, or paste a cue JSON above and hit <em>Apply</em>.
            </div>
          </div>
        </div>

        <!-- 4. Fallback -->
        <div class="editor-section">
          <div class="section-head">
            <strong>4. Fallback (when no master audio)</strong>
            <span class="section-note">Each cue runs for this many seconds if there's no master audio.</span>
          </div>
          <div class="field-inline">
            <label class="inline-label">Fallback slide duration (s)</label>
            <input
              type="number"
              class="num"
              min="1"
              step="0.5"
              :value="fallbackSlideDurationMs / 1000"
              @input="updateFallbackDuration($event.target.value)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SlideRenderer from '@/components/video/SlideRenderer.vue'
import sampleScript from './sampleScript'
import { postRequest } from '@/apis/front'

const COUNTDOWN_SECONDS = 4
const ESC_CONFIRM_WINDOW_MS = 1500
// How far the mouse must move (px) during arming before we treat it as a real
// abort. Small values trigger on fullscreen layout-shift ghosts.
const ARMING_MOTION_THRESHOLD_PX = 40
// Ignore any input events fired in this window after Record is clicked.
// Covers: trailing mousedown/mouseup from the click itself, plus the
// mousemove that browsers synthesise when the page enters fullscreen.
const ARMING_GRACE_MS = 400

export default {
  name: 'VideoPlayer',
  components: { SlideRenderer },
  data() {
    return {
      script: sampleScript,
      currentIndex: 0,
      playing: false,
      // 'idle' | 'arming' (counting down) | 'active' (recording playback)
      recordingPhase: 'idle',
      countdownValue: COUNTDOWN_SECONDS,
      countdownTimer: null,
      armingStartAt: 0,
      armingAnchor: null,
      lastAbortReason: '',
      lastAbortClearTimer: null,
      escConfirmPending: false,
      escConfirmTimer: null,
      scale: 1,
      slideStartAt: 0,
      slideElapsed: 0,
      slideDuration: 1,
      animationFrame: null,
      pauseTimer: null,
      advanceTimer: null,
      // ── Master audio + cue list (primary authoring model) ──────
      // Session-only, blob URLs. Persists nowhere.
      editorOpen: false,
      showCaptions: true,
      masterAudioBlob: null,
      masterAudioUrl: '',
      masterAudioName: '',
      masterAudioDurationMs: 0,
      masterCurrentMs: 0,
      slidesBundleBlob: null,
      slidesBundleUrl: '',
      slidesBundleName: '',
      slidesBundleReady: false,
      detectedSlideIds: [],
      cues: [],
      // ── Native recording (MediaRecorder + getDisplayMedia) ──────
      audioCtx: null,
      audioSourceNode: null,
      audioStreamDest: null,
      captureStream: null,
      mediaRecorder: null,
      recordedChunks: [],
      recordingToFile: false,
      lastRecordingUrl: '',
      lastRecordingName: '',
      recordingSavedToast: false,
      recordingError: '',
      cueSeq: 0,
      cueImportText: '',
      cueImportError: '',
      fallbackSlideDurationMs: 5000,
      // ── Auto-loaded mode (from ?collection_id=…) ──────────────
      // When the player is opened with a collection_id query param, we fetch
      // the collection's video.bundle_html + video.slides[] from the backend
      // and pre-populate the editor. The player still works the same way after
      // — this just removes the file-picker step for the common case.
      autoLoadedCollection: null,   // { id, title, status, youtube_url } | null
      autoLoadError: ''
    }
  },
  computed: {
    masterMode() {
      return !!this.masterAudioUrl && this.cues.length > 0
    },
    slides() {
      if (this.cues.length) {
        // Derive slide list from cues. Duration between cue N and cue N+1.
        // Last cue runs to master audio end (or fallback if no master).
        const endMs = this.masterAudioDurationMs || 0
        return this.cues.map((c, i) => {
          const next = this.cues[i + 1]
          let dur
          if (next) dur = Math.max(1, next.atMs - c.atMs)
          else if (endMs > c.atMs) dur = endMs - c.atMs
          else dur = this.fallbackSlideDurationMs
          return {
            id: c.id,
            type: 'content',
            audio_url: '', // audio is master-level, not per-slide
            duration_ms: dur,
            fallback_duration_ms: this.fallbackSlideDurationMs,
            subtitle: c.subtitle || '',
            at_ms: c.atMs,
            visual: {
              template: 'html',
              html_url: c.htmlUrl || ''
            }
          }
        })
      }
      return this.script.slides || []
    },
    currentSlide() {
      return this.slides[this.currentIndex] || null
    },
    currentSubtitle() {
      return (this.currentSlide && this.currentSlide.subtitle) || ''
    },
    transitionName() {
      const t = this.script.video_config?.transition || 'fade'
      return `slide-${t}`
    },
    stageOuterStyle() {
      // Real on-screen size of the 16:9 stage. CropTarget reads this.
      return {
        width: (1920 * this.scale) + 'px',
        height: (1080 * this.scale) + 'px'
      }
    },
    stageContentStyle() {
      // Fixed 1920x1080 inner surface, visually scaled to match the outer.
      return {
        width: '1920px',
        height: '1080px',
        transform: `scale(${this.scale})`,
        transformOrigin: 'top left'
      }
    },
    slideProgress() {
      return Math.min(1, this.slideElapsed / Math.max(1, this.slideDuration))
    },
    overallProgress() {
      if (this.masterMode && this.masterAudioDurationMs) {
        return Math.min(1, this.masterCurrentMs / this.masterAudioDurationMs)
      }
      if (!this.slides.length) return 0
      return (this.currentIndex + this.slideProgress) / this.slides.length
    },
    sampleJsonPlaceholder() {
      return '{\n  "version": "1.1",\n  "source_audio": "narration.mp3",\n  "slides_bundle": "slides.html",\n  "audio_duration_ms": 162000,\n  "slides": [\n    { "id": "s1", "at_ms": 0,     "step": 0 },\n    { "id": "s4", "at_ms": 47480, "step": 0 },\n    { "id": "s4", "at_ms": 50000, "step": 1 },\n    { "id": "s4", "at_ms": 54000, "step": 2 }\n  ]\n}'
    }
  },
  mounted() {
    this.updateScale()
    window.addEventListener('resize', this.updateScale)
    window.addEventListener('keydown', this.onKey)
    window.addEventListener('message', this.onIframeMessage)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    // Auto-load a collection's bundle_html + slides[] when the route has
    // ?collection_id=…  (set by the "Open in Video Player" button on the
    // collection-editing page).
    const collectionId = (this.$route && this.$route.query && this.$route.query.collection_id) || ''
    if (collectionId) {
      this.loadFromCollection(collectionId)
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScale)
    window.removeEventListener('keydown', this.onKey)
    window.removeEventListener('message', this.onIframeMessage)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    this.detachArmingAbortListeners()
    this.clearTimers()
    this.clearCountdown()
    this.clearEscConfirm()
    this.cancelFrame()
    if (this.masterAudioUrl) URL.revokeObjectURL(this.masterAudioUrl)
    if (this.slidesBundleUrl) URL.revokeObjectURL(this.slidesBundleUrl)
    if (this.lastRecordingUrl) URL.revokeObjectURL(this.lastRecordingUrl)
    this.cues.forEach((c) => { if (c.htmlUrl) URL.revokeObjectURL(c.htmlUrl) })
    this.stopMediaRecorder()
    this.teardownCaptureStream()
    if (this.audioCtx && this.audioCtx.state !== 'closed') {
      try { this.audioCtx.close() } catch (_) { /* noop */ }
    }
  },
  watch: {
    currentIndex() {
      this.pushSlideToBundle()
    }
  },
  methods: {
    updateScale() {
      const stage = this.$refs.stage
      if (!stage) return
      const rect = stage.getBoundingClientRect()
      const sx = rect.width / 1920
      const sy = rect.height / 1080
      this.scale = Math.min(sx, sy)
    },

    // ─── Playback ───────────────────────────────────────────────
    togglePlay() {
      if (this.playing) this.pause()
      else this.play()
    },
    play() {
      if (!this.currentSlide) return
      // If Web Audio routing was set up (for recording) and the context got
      // suspended, resume it so the audio element actually outputs sound.
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        this.audioCtx.resume().catch(() => { /* noop */ })
      }
      this.playing = true
      if (this.masterMode) this.startMasterFromCurrent()
      else this.startSlide()
    },
    pause() {
      this.playing = false
      const audio = this.$refs.audio
      if (audio && !audio.paused) audio.pause()
      this.clearTimers()
      this.cancelFrame()
    },
    restart() {
      this.pause()
      this.currentIndex = 0
      this.slideElapsed = 0
      this.masterCurrentMs = 0
      if (this.masterMode) {
        const audio = this.$refs.audio
        if (audio) audio.currentTime = 0
      }
    },

    // Master-audio mode: one audio element plays continuously, cues drive
    // slide switches via currentTime.
    startMasterFromCurrent() {
      this.clearTimers()
      this.cancelFrame()
      const audio = this.$refs.audio
      const cue = this.cues[this.currentIndex]
      if (!cue) { this.playing = false; return }
      const seekSec = cue.atMs / 1000
      if (audio.src !== this.masterAudioUrl) audio.src = this.masterAudioUrl
      try { audio.currentTime = seekSec } catch (_) { /* noop */ }
      audio.play().catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[VideoPlayer] master audio play failed', err)
        this.playing = false
      })
      this.tickMaster()
    },
    tickMaster() {
      this.animationFrame = requestAnimationFrame(() => {
        if (!this.playing) return
        const audio = this.$refs.audio
        const nowMs = audio.currentTime * 1000
        this.masterCurrentMs = nowMs
        // Find latest cue whose atMs <= nowMs.
        let idx = 0
        for (let i = 0; i < this.cues.length; i++) {
          if (this.cues[i].atMs <= nowMs) idx = i
          else break
        }
        if (idx !== this.currentIndex) this.currentIndex = idx
        const cur = this.cues[idx]
        const next = this.cues[idx + 1]
        const endMs = next ? next.atMs : (this.masterAudioDurationMs || nowMs + 1)
        this.slideElapsed = Math.max(0, nowMs - cur.atMs)
        this.slideDuration = Math.max(1, endMs - cur.atMs)
        this.tickMaster()
      })
    },

    // Per-slide / fallback mode.
    startSlide() {
      this.clearTimers()
      this.cancelFrame()
      const slide = this.currentSlide
      if (!slide) {
        this.playing = false
        return
      }
      this.slideStartAt = performance.now()
      this.slideElapsed = 0
      this.slideDuration = slide.duration_ms || slide.fallback_duration_ms || 5000

      const audio = this.$refs.audio
      if (slide.audio_url) {
        audio.src = slide.audio_url
        audio.currentTime = 0
        audio.play().catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[VideoPlayer] audio play failed', err)
          this.startFallbackTimer()
        })
      } else {
        audio.removeAttribute('src')
        this.startFallbackTimer()
      }
      this.tickFallbackProgress()
    },
    startFallbackTimer() {
      this.advanceTimer = setTimeout(() => {
        this.onSlideEnded()
      }, this.slideDuration)
    },
    onAudioMeta() {
      // Only meaningful in per-slide mode; master-mode duration comes from
      // masterAudioDurationMs (captured on upload).
      if (this.masterMode) return
      const audio = this.$refs.audio
      if (audio && audio.duration && isFinite(audio.duration)) {
        this.slideDuration = audio.duration * 1000
      }
    },
    onAudioEnded() {
      if (this.masterMode) {
        // End of master audio → stop playback + wrap up any active recording.
        this.playing = false
        this.cancelFrame()
        this.onPlaybackCompleted()
        return
      }
      this.onSlideEnded()
    },
    onSlideEnded() {
      if (!this.playing) return
      const pause = this.script.video_config?.pause_between_slides_ms ?? 300
      this.pauseTimer = setTimeout(() => {
        if (this.currentIndex < this.slides.length - 1) {
          this.currentIndex += 1
          this.startSlide()
        } else {
          this.playing = false
          this.onPlaybackCompleted()
        }
      }, pause)
    },
    tickFallbackProgress() {
      this.animationFrame = requestAnimationFrame(() => {
        if (!this.playing) return
        const audio = this.$refs.audio
        if (audio && audio.src && !audio.paused) {
          this.slideElapsed = audio.currentTime * 1000
        } else {
          this.slideElapsed = performance.now() - this.slideStartAt
        }
        this.tickFallbackProgress()
      })
    },
    nextSlide() {
      if (this.currentIndex >= this.slides.length - 1) return
      this.currentIndex += 1
      if (this.playing) {
        if (this.masterMode) this.startMasterFromCurrent()
        else this.startSlide()
      } else {
        this.slideElapsed = 0
        if (this.masterMode) {
          const audio = this.$refs.audio
          const cue = this.cues[this.currentIndex]
          if (audio && cue) { try { audio.currentTime = cue.atMs / 1000 } catch (_) { /* noop */ } }
        }
      }
    },
    prevSlide() {
      if (this.currentIndex === 0) return
      this.currentIndex -= 1
      if (this.playing) {
        if (this.masterMode) this.startMasterFromCurrent()
        else this.startSlide()
      } else {
        this.slideElapsed = 0
        if (this.masterMode) {
          const audio = this.$refs.audio
          const cue = this.cues[this.currentIndex]
          if (audio && cue) { try { audio.currentTime = cue.atMs / 1000 } catch (_) { /* noop */ } }
        }
      }
    },
    clearTimers() {
      if (this.pauseTimer) {
        clearTimeout(this.pauseTimer)
        this.pauseTimer = null
      }
      if (this.advanceTimer) {
        clearTimeout(this.advanceTimer)
        this.advanceTimer = null
      }
    },
    cancelFrame() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame)
        this.animationFrame = null
      }
    },

    // ─── Native capture (MediaRecorder + getDisplayMedia) ───────
    ensureAudioRouting() {
      // One-time setup. createMediaElementSource can only be called once per
      // element; reuse the node forever after.
      if (this.audioSourceNode) return true
      const audio = this.$refs.audio
      if (!audio) return false
      const Ctx = window.AudioContext || window.webkitAudioContext
      if (!Ctx) return false
      this.audioCtx = new Ctx()
      this.audioSourceNode = this.audioCtx.createMediaElementSource(audio)
      this.audioStreamDest = this.audioCtx.createMediaStreamDestination()
      // Fan out: one branch to speakers, one to the recorder.
      this.audioSourceNode.connect(this.audioCtx.destination)
      this.audioSourceNode.connect(this.audioStreamDest)
      return true
    },
    pickRecorderMime() {
      // VP8 preserves text/slide crispness better than VP9 on static content.
      // VP9 aggressively under-bitrates near-static frames; VP8's simpler
      // encoder holds sharper detail when slides sit still for seconds.
      const candidates = [
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9,opus',
        'video/webm'
      ]
      for (const m of candidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m
      }
      return ''
    },
    async acquireCaptureStream() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        throw new Error('getDisplayMedia is not available in this browser')
      }
      if (!window.MediaRecorder) {
        throw new Error('MediaRecorder is not available in this browser')
      }
      // Force tab capture (displaySurface: 'browser') + auto-select current
      // tab. Ask for up to 4K so Chrome captures at device-pixel fidelity on
      // Retina displays — Region Capture then crops that high-res stream to
      // the 1920x1080 stage-inner, preserving sub-pixel text crispness.
      const constraints = {
        video: {
          displaySurface: 'browser',
          width:  { ideal: 3840, max: 3840 },
          height: { ideal: 2160, max: 2160 },
          frameRate: { ideal: 30 }
        },
        // We don't need system audio — narration is piped in via Web Audio.
        audio: false,
        preferCurrentTab: true,
        surfaceSwitching: 'exclude',
        selfBrowserSurface: 'include'
      }
      const stream = await navigator.mediaDevices.getDisplayMedia(constraints)
      // Log what Chrome actually gave us — lets the user verify source res
      // via the browser console (and by later ffprobing the .webm).
      const vtrack = stream.getVideoTracks()[0]
      if (vtrack) {
        const s = vtrack.getSettings ? vtrack.getSettings() : {}
        // eslint-disable-next-line no-console
        console.log('[VideoPlayer] tab capture settings:', {
          width: s.width, height: s.height, frameRate: s.frameRate,
          displaySurface: s.displaySurface
        })
      }
      // Crop the capture to just the slide area, removing all page chrome,
      // controls, letterbox, and the Chrome "sharing" indicator.
      await this.cropStreamToStage(stream)
      if (vtrack && vtrack.getSettings) {
        const s2 = vtrack.getSettings()
        // eslint-disable-next-line no-console
        console.log('[VideoPlayer] post-crop settings:', {
          width: s2.width, height: s2.height
        })
      }
      return stream
    },
    async cropStreamToStage(stream) {
      const videoTrack = stream.getVideoTracks()[0]
      if (!videoTrack) return
      // Feature-detect Region Capture (Chrome 104+).
      const CropTargetCtor = window.CropTarget
      if (!CropTargetCtor || typeof CropTargetCtor.fromElement !== 'function') {
        // eslint-disable-next-line no-console
        console.warn('[VideoPlayer] CropTarget not supported — recording the whole tab')
        return
      }
      if (typeof videoTrack.cropTo !== 'function') {
        // eslint-disable-next-line no-console
        console.warn('[VideoPlayer] videoTrack.cropTo not supported — recording the whole tab')
        return
      }
      const el = this.$refs.stageInner
      if (!el) return
      try {
        const target = await CropTargetCtor.fromElement(el)
        await videoTrack.cropTo(target)
        // eslint-disable-next-line no-console
        console.log('[VideoPlayer] cropped capture to stage-inner element')
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[VideoPlayer] cropTo failed — recording the whole tab', err)
      }
    },
    buildRecorder(videoStream) {
      const audioTracks = this.audioStreamDest
        ? this.audioStreamDest.stream.getAudioTracks()
        : []
      const combined = new MediaStream([
        ...videoStream.getVideoTracks(),
        ...audioTracks
      ])
      const mimeType = this.pickRecorderMime()
      // Slides are mostly static → VP9 will compress aggressively and blur
      // text at low bitrates. Setting a high target keeps text crisp.
      // 20 Mbps is YouTube's own recommendation for 1080p SDR uploads.
      const opts = { videoBitsPerSecond: 20_000_000, audioBitsPerSecond: 192_000 }
      if (mimeType) opts.mimeType = mimeType
      const rec = new MediaRecorder(combined, opts)
      this.recordedChunks = []
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size) this.recordedChunks.push(e.data)
      }
      rec.onstop = () => this.finalizeRecording()
      // If the user stops sharing via the browser's own UI, end cleanly.
      const vt = videoStream.getVideoTracks()[0]
      if (vt) {
        vt.onended = () => {
          if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            try { this.mediaRecorder.stop() } catch (_) { /* noop */ }
          }
          // Also stop playback if it's still running.
          if (this.playing) this.pause()
          if (this.recordingPhase === 'active') this.exitRecording()
        }
      }
      return rec
    },
    startMediaRecorder() {
      if (!this.mediaRecorder) return
      if (this.mediaRecorder.state === 'inactive') {
        try { this.mediaRecorder.start(1000) } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[VideoPlayer] mediaRecorder.start failed', err)
        }
      }
    },
    stopMediaRecorder() {
      if (!this.mediaRecorder) return
      if (this.mediaRecorder.state !== 'inactive') {
        try { this.mediaRecorder.stop() } catch (_) { /* noop */ }
      }
    },
    teardownCaptureStream() {
      if (this.captureStream) {
        this.captureStream.getTracks().forEach((t) => { try { t.stop() } catch (_) { /* noop */ } })
        this.captureStream = null
      }
    },
    finalizeRecording() {
      // Build one blob from the chunks and kick off a download.
      if (!this.recordedChunks.length) {
        this.teardownCaptureStream()
        this.mediaRecorder = null
        this.recordingToFile = false
        return
      }
      const type = (this.mediaRecorder && this.mediaRecorder.mimeType) || 'video/webm'
      const blob = new Blob(this.recordedChunks, { type })
      if (this.lastRecordingUrl) URL.revokeObjectURL(this.lastRecordingUrl)
      this.lastRecordingUrl = URL.createObjectURL(blob)
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
      this.lastRecordingName = `video-${stamp}.webm`
      // Auto-download.
      const a = document.createElement('a')
      a.href = this.lastRecordingUrl
      a.download = this.lastRecordingName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      // Reset chunks, clean up capture tracks.
      this.recordedChunks = []
      this.teardownCaptureStream()
      this.mediaRecorder = null
      this.recordingToFile = false
      this.recordingSavedToast = true
      setTimeout(() => { this.recordingSavedToast = false }, 8000)
    },

    // ─── Recording flow ─────────────────────────────────────────
    async startRecordFlow() {
      if (this.recordingPhase !== 'idle') return
      // Stop any casual playback first.
      this.pause()
      this.recordingError = ''
      // Clear any lingering abort reason from a prior attempt.
      this.lastAbortReason = ''
      if (this.lastAbortClearTimer) {
        clearTimeout(this.lastAbortClearTimer)
        this.lastAbortClearTimer = null
      }

      // (A) Set up Web Audio routing once (needed so the narration ends up in
      // the recorded file — not just through the speakers).
      this.ensureAudioRouting()
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        try { await this.audioCtx.resume() } catch (_) { /* noop */ }
      }

      // (B) Ask the user what to capture. This opens a browser picker and
      // consumes the user gesture. We do this BEFORE fullscreen so the picker
      // is visible.
      try {
        this.captureStream = await this.acquireCaptureStream()
        this.mediaRecorder = this.buildRecorder(this.captureStream)
        this.recordingToFile = true
      } catch (err) {
        // User cancelled the picker, or API unavailable. Abort cleanly.
        // eslint-disable-next-line no-console
        console.warn('[VideoPlayer] capture not acquired:', err && err.message)
        this.recordingError = err && err.message ? err.message : 'Screen capture was cancelled'
        setTimeout(() => { this.recordingError = '' }, 5000)
        this.teardownCaptureStream()
        return
      }

      // (C) Try fullscreen (best-effort — some user-activation rules may
      // prevent it after the picker; that's fine, tab capture still works).
      try {
        const el = this.$refs.root
        if (el && el.requestFullscreen && !document.fullscreenElement) {
          await el.requestFullscreen()
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[VideoPlayer] fullscreen request failed', err)
      }
      // (D) Capture Escape key.
      try {
        if (navigator.keyboard && navigator.keyboard.lock) {
          await navigator.keyboard.lock(['Escape'])
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[VideoPlayer] keyboard lock failed', err)
      }
      this.recordingPhase = 'arming'
      this.countdownValue = COUNTDOWN_SECONDS
      this.armingStartAt = performance.now()
      this.armingAnchor = null
      this.attachArmingAbortListeners()
      this.countdownTimer = setInterval(() => {
        this.countdownValue -= 1
        if (this.countdownValue <= 0) {
          this.completeCountdown()
        }
      }, 1000)
    },
    cancelCountdown() {
      if (this.recordingPhase !== 'arming') return
      this.clearCountdown()
      this.detachArmingAbortListeners()
      this.exitFullscreenSafe()
      // User bailed out before recording ever started — drop the capture.
      this.teardownCaptureStream()
      this.mediaRecorder = null
      this.recordingToFile = false
      this.recordingPhase = 'idle'
    },
    completeCountdown() {
      this.clearCountdown()
      this.detachArmingAbortListeners()
      this.recordingPhase = 'active'
      this.currentIndex = 0
      this.slideElapsed = 0
      this.masterCurrentMs = 0
      // Start the recorder the instant the countdown finishes, then kick off
      // playback on the next tick so the countdown overlay is already gone.
      if (this.recordingToFile) this.startMediaRecorder()
      this.$nextTick(() => this.play())
    },
    exitRecording() {
      this.pause()
      this.clearEscConfirm()
      this.exitFullscreenSafe()
      // Stop the recorder (triggers finalizeRecording → download).
      if (this.recordingToFile) this.stopMediaRecorder()
      this.recordingPhase = 'idle'
    },
    onPlaybackCompleted() {
      // Called when the master audio ends naturally. If we're recording, wrap
      // up the file and exit recording mode.
      if (this.recordingPhase === 'active' && this.recordingToFile) {
        // Tiny delay so the final frame is in the recording.
        setTimeout(() => {
          this.stopMediaRecorder()
          this.exitFullscreenSafe()
          this.recordingPhase = 'idle'
        }, 400)
      }
    },
    attachArmingAbortListeners() {
      // If we're recording to a file, the user already confirmed intent by
      // clicking Share in the browser picker — skip motion/click aborts so
      // the "sharing this tab" bar appearing doesn't jostle the cursor into
      // aborting the countdown. Keyboard abort (via onKey) stays on.
      if (this.recordingToFile) return
      window.addEventListener('mousemove', this.onArmingMouseMove, true)
      window.addEventListener('mousedown', this.onArmingInput, true)
      window.addEventListener('touchstart', this.onArmingInput, true)
    },
    detachArmingAbortListeners() {
      window.removeEventListener('mousemove', this.onArmingMouseMove, true)
      window.removeEventListener('mousedown', this.onArmingInput, true)
      window.removeEventListener('touchstart', this.onArmingInput, true)
    },
    isWithinArmingGrace() {
      return performance.now() - this.armingStartAt < ARMING_GRACE_MS
    },
    onArmingMouseMove(e) {
      // Seed the anchor on the first mousemove we see *after* the grace window,
      // so fullscreen-layout-shift mousemoves are discarded.
      if (this.isWithinArmingGrace()) return
      if (!this.armingAnchor) {
        this.armingAnchor = { x: e.clientX, y: e.clientY }
        return
      }
      const dx = e.clientX - this.armingAnchor.x
      const dy = e.clientY - this.armingAnchor.y
      const dist = Math.hypot(dx, dy)
      if (dist >= ARMING_MOTION_THRESHOLD_PX) {
        this.showAbortReason(`mousemove Δ${Math.round(dist)}px @ (${e.clientX}, ${e.clientY}) from anchor (${this.armingAnchor.x}, ${this.armingAnchor.y})`)
        this.cancelCountdown()
      }
    },
    onArmingInput(e) {
      if (this.isWithinArmingGrace()) return
      const where = e.clientX != null ? ` @ (${e.clientX}, ${e.clientY})` : ''
      this.showAbortReason(`${e.type}${where} · target=${(e.target && e.target.tagName) || '?'}`)
      this.cancelCountdown()
    },
    showAbortReason(text) {
      const elapsed = Math.round(performance.now() - this.armingStartAt)
      this.lastAbortReason = `[${elapsed}ms into arming] ${text}`
      if (this.lastAbortClearTimer) clearTimeout(this.lastAbortClearTimer)
      this.lastAbortClearTimer = setTimeout(() => {
        this.lastAbortReason = ''
        this.lastAbortClearTimer = null
      }, 6000)
      // eslint-disable-next-line no-console
      console.log('[VideoPlayer] countdown aborted:', this.lastAbortReason)
    },
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    exitFullscreenSafe() {
      if (navigator.keyboard && navigator.keyboard.unlock) {
        try { navigator.keyboard.unlock() } catch (_) { /* noop */ }
      }
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {})
      }
    },
    onFullscreenChange() {
      if (document.fullscreenElement) return
      // Fullscreen exited — if we were arming or actively recording, clean up.
      // On Chrome with Keyboard Lock this shouldn't fire for Esc during active
      // recording; on Firefox/Safari it will, and that's the right behavior.
      if (this.recordingPhase === 'arming') {
        this.showAbortReason('fullscreen exited during arming')
        this.cancelCountdown()
      } else if (this.recordingPhase === 'active') {
        this.exitRecording()
      }
    },

    // ─── ESC confirm ────────────────────────────────────────────
    armEscConfirm() {
      this.escConfirmPending = true
      this.clearEscConfirm(false)
      this.escConfirmTimer = setTimeout(() => {
        this.escConfirmPending = false
        this.escConfirmTimer = null
      }, ESC_CONFIRM_WINDOW_MS)
    },
    clearEscConfirm(resetFlag = true) {
      if (this.escConfirmTimer) {
        clearTimeout(this.escConfirmTimer)
        this.escConfirmTimer = null
      }
      if (resetFlag) this.escConfirmPending = false
    },

    // ─── Auto-load from a collection's video.bundle_html ──────
    // Triggered by ?collection_id=… in the URL. Fetches the collection,
    // converts video.bundle_html into a Blob URL (same shape onSlidesBundleFile
    // produces from the file picker), and seeds the cue list from
    // video.slides[]'s duration_ms so the player can step through without
    // a master audio track. Master audio remains a manual upload — the auto-
    // load only handles slides + cues.
    async loadFromCollection(collectionId) {
      this.autoLoadError = ''
      try {
        const resp = await postRequest('getPostCollection', {
          id: collectionId,
          user_id: (this.$store && this.$store.state && this.$store.state.user_id) || ''
        })
        const data = resp && resp.data
        if (!data || data.code !== 0) {
          this.autoLoadError = (data && data.msg) || 'Failed to load collection'
          return
        }
        const coll = data.data || {}
        const video = coll.video
        if (!video || !video.bundle_html) {
          this.autoLoadError = 'This collection has no long-form video yet (video.bundle_html is empty).'
          return
        }
        // Convert bundle_html string → Blob → object URL, mirroring onSlidesBundleFile.
        const blob = new Blob([video.bundle_html], { type: 'text/html' })
        if (this.slidesBundleUrl) URL.revokeObjectURL(this.slidesBundleUrl)
        this.slidesBundleBlob = blob
        this.slidesBundleUrl = URL.createObjectURL(blob)
        this.slidesBundleName = (coll.title || 'collection') + '.slides.html'
        this.slidesBundleReady = false
        this.detectedSlideIds = []
        // Extract slide IDs from the bundle for the editor hint (same as the file path).
        const text = video.bundle_html
        const ids = []
        const re = /data-slide-id=["']([^"']+)["']/g
        let m
        while ((m = re.exec(text)) !== null) ids.push(m[1])
        this.detectedSlideIds = ids
        // Seed cues from video.slides[] (each slide has duration_ms; convert to atMs offsets).
        const slides = Array.isArray(video.slides) ? video.slides : []
        if (slides.length) {
          let cumulative = 0
          this.cues = slides.map((s, i) => {
            const cue = {
              id: ++this.cueSeq,
              atMs: cumulative,
              slideId: s.id || '',
              subtitle: s.title || '',
              htmlUrl: ''
            }
            cumulative += Number(s.duration_ms) || this.fallbackSlideDurationMs
            return cue
          })
        }
        this.autoLoadedCollection = {
          id: collectionId,
          title: coll.title || '',
          status: (video.status || 'DRAFT'),
          youtube_url: video.youtube_url || ''
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('VideoPlayer auto-load failed:', err)
        this.autoLoadError = 'Network error while loading the collection.'
      }
    },

    // ─── Slides bundle (single HTML, postMessage protocol) ─────
    onSlidesBundleFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (this.slidesBundleUrl) URL.revokeObjectURL(this.slidesBundleUrl)
      this.slidesBundleBlob = file
      this.slidesBundleUrl = URL.createObjectURL(file)
      this.slidesBundleName = file.name
      this.slidesBundleReady = false
      this.detectedSlideIds = []
      // Parse once to extract slide IDs for the UI hint.
      file.text().then((text) => {
        const ids = []
        const re = /data-slide-id=["']([^"']+)["']/g
        let m
        while ((m = re.exec(text)) !== null) ids.push(m[1])
        this.detectedSlideIds = ids
      }).catch(() => {})
    },
    removeSlidesBundle() {
      if (this.slidesBundleUrl) URL.revokeObjectURL(this.slidesBundleUrl)
      this.slidesBundleBlob = null
      this.slidesBundleUrl = ''
      this.slidesBundleName = ''
      this.slidesBundleReady = false
      this.detectedSlideIds = []
    },
    onBundleLoad() {
      // The iframe's inline script posts a 'slides_ready' handshake on load.
      // Push the current slide right away as a belt-and-suspenders in case the
      // handshake didn't arrive (e.g. sandbox restrictions).
      this.slidesBundleReady = true
      this.pushSlideToBundle()
    },
    onIframeMessage(e) {
      const d = e && e.data
      if (!d || typeof d !== 'object') return
      if (d.type === 'slides_ready') {
        this.slidesBundleReady = true
        if (Array.isArray(d.ids) && d.ids.length) this.detectedSlideIds = d.ids
        this.pushSlideToBundle()
      }
      // 'slide_shown' acks are ignored for now; add telemetry later if needed.
    },
    pushSlideToBundle() {
      if (!this.slidesBundleUrl) return
      const frame = this.$refs.bundleIframe
      if (!frame || !frame.contentWindow) return
      const cue = this.cues[this.currentIndex]
      if (!cue || !cue.slideId) return
      try {
        frame.contentWindow.postMessage({
          type: 'show_slide',
          id: cue.slideId,
          step: Number.isFinite(cue.step) ? cue.step : 0
        }, '*')
      } catch (_) { /* noop */ }
    },

    // ─── Master audio + cue list editor ─────────────────────────
    onMasterAudioFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (this.masterAudioUrl) URL.revokeObjectURL(this.masterAudioUrl)
      this.masterAudioBlob = file
      this.masterAudioUrl = URL.createObjectURL(file)
      this.masterAudioName = file.name
      this.masterAudioDurationMs = 0
      const probe = new Audio()
      probe.preload = 'metadata'
      probe.src = this.masterAudioUrl
      probe.onloadedmetadata = () => {
        if (isFinite(probe.duration)) {
          this.masterAudioDurationMs = Math.round(probe.duration * 1000)
        }
      }
    },
    removeMasterAudio() {
      if (this.masterAudioUrl) URL.revokeObjectURL(this.masterAudioUrl)
      this.masterAudioBlob = null
      this.masterAudioUrl = ''
      this.masterAudioName = ''
      this.masterAudioDurationMs = 0
    },
    addCue(atMs) {
      this.cueSeq += 1
      const defaultAt = typeof atMs === 'number'
        ? atMs
        : this.cues.length ? (this.cues[this.cues.length - 1].atMs + 5000) : 0
      this.cues.push({
        id: `c-${this.cueSeq}`,
        atMs: Math.max(0, Math.round(defaultAt)),
        slideId: '',
        step: 0,
        htmlBlob: null,
        htmlUrl: '',
        htmlName: '',
        subtitle: ''
      })
      this.sortCues()
    },
    updateCueSlideId(idx, value) {
      this.$set(this.cues[idx], 'slideId', (value || '').trim())
      if (idx === this.currentIndex) this.pushSlideToBundle()
    },
    updateCueStep(idx, value) {
      const n = parseInt(value, 10)
      this.$set(this.cues[idx], 'step', Number.isFinite(n) && n >= 0 ? n : 0)
      if (idx === this.currentIndex) this.pushSlideToBundle()
    },
    onCueHtmlFile(event, idx) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const cue = this.cues[idx]
      if (cue.htmlUrl) URL.revokeObjectURL(cue.htmlUrl)
      cue.htmlBlob = file
      cue.htmlUrl = URL.createObjectURL(file)
      cue.htmlName = file.name
    },
    updateCueSubtitle(idx, value) {
      this.$set(this.cues[idx], 'subtitle', value)
    },
    updateCueAtMs(idx, seconds) {
      const n = parseFloat(seconds)
      if (!isFinite(n) || n < 0) return
      this.$set(this.cues[idx], 'atMs', Math.round(n * 1000))
    },
    sortCues() {
      this.cues = this.cues.slice().sort((a, b) => a.atMs - b.atMs)
    },
    moveCue(idx, delta) {
      // Swap ordering and also swap timestamps so order stays consistent with
      // time. If the user really wants to change a timestamp, they edit the
      // number field directly.
      const to = idx + delta
      if (to < 0 || to >= this.cues.length) return
      const copy = this.cues.slice()
      const a = copy[idx]
      const b = copy[to]
      const savedAtA = a.atMs
      a.atMs = b.atMs
      b.atMs = savedAtA
      this.cues = copy.sort((x, y) => x.atMs - y.atMs)
    },
    removeCue(idx) {
      const [cue] = this.cues.splice(idx, 1)
      if (cue && cue.htmlUrl) URL.revokeObjectURL(cue.htmlUrl)
      if (this.currentIndex >= this.slides.length) {
        this.currentIndex = Math.max(0, this.slides.length - 1)
      }
    },
    clearCues() {
      this.cues.forEach((c) => { if (c.htmlUrl) URL.revokeObjectURL(c.htmlUrl) })
      this.cues = []
      this.currentIndex = 0
    },
    triggerCueJsonUpload() {
      const input = this.$refs.cueJsonInput
      if (input) input.click()
    },
    onCueJsonFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      // eslint-disable-next-line no-console
      console.log('[VideoPlayer] reading cue JSON file:', file.name, file.size, 'bytes')
      file.text().then((text) => {
        this.cueImportText = text
        this.applyCueImport()
        // Reset so selecting the same file again still fires @change.
        event.target.value = ''
      }).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[VideoPlayer] file.text() failed', err)
        this.cueImportError = 'Could not read file: ' + (err && err.message)
        event.target.value = ''
      })
    },
    applyCueImport() {
      this.cueImportError = ''
      let parsed
      try {
        parsed = JSON.parse(this.cueImportText)
      } catch (err) {
        this.cueImportError = 'Invalid JSON: ' + err.message
        return
      }
      // Accept three shapes:
      //   v1.1: { version: "1.1", slides_bundle, slides: [{ id, at_ms }] }
      //   bare array [{ at_ms, slide_id?/html?, subtitle? }]
      //   legacy { cues: [...] }
      let rawRows, bundleHint = null
      if (Array.isArray(parsed)) {
        rawRows = parsed
      } else if (parsed && Array.isArray(parsed.slides)) {
        rawRows = parsed.slides
        if (typeof parsed.slides_bundle === 'string') bundleHint = parsed.slides_bundle
      } else if (parsed && Array.isArray(parsed.cues)) {
        rawRows = parsed.cues
      } else {
        this.cueImportError = 'Expected an array, or { "slides": [...] }, or { "cues": [...] }'
        return
      }
      if (bundleHint && !this.slidesBundleName) {
        // Informational — just remind the user to attach the bundle file.
        this.cueImportError = `JSON references slides_bundle "${bundleHint}" — upload that file in step 2.`
      }
      const next = []
      for (let i = 0; i < rawRows.length; i++) {
        const row = rawRows[i] || {}
        const atMs = typeof row.at_ms === 'number'
          ? row.at_ms
          : (typeof row.at === 'number' ? Math.round(row.at * 1000) : 0)
        const slideId = typeof row.id === 'string' ? row.id
          : typeof row.slide_id === 'string' ? row.slide_id
          : ''
        const step = Number.isFinite(+row.step) ? Math.max(0, parseInt(row.step, 10)) : 0
        const existing = this.cues[i]
        this.cueSeq += 1
        next.push({
          id: existing ? existing.id : `c-${this.cueSeq}`,
          atMs: Math.max(0, Math.round(atMs)),
          slideId: slideId || (existing ? existing.slideId : ''),
          step,
          htmlBlob: existing ? existing.htmlBlob : null,
          htmlUrl: existing ? existing.htmlUrl : '',
          htmlName: existing ? existing.htmlName : (row.html || ''),
          subtitle: typeof row.subtitle === 'string' ? row.subtitle : (existing ? existing.subtitle : '')
        })
      }
      for (let i = rawRows.length; i < this.cues.length; i++) {
        if (this.cues[i].htmlUrl) URL.revokeObjectURL(this.cues[i].htmlUrl)
      }
      this.cues = next.sort((a, b) => a.atMs - b.atMs)
      this.$nextTick(() => this.pushSlideToBundle())
    },
    exportCueJson() {
      const payload = {
        version: '1.1',
        source_audio: this.masterAudioName || null,
        slides_bundle: this.slidesBundleName || null,
        audio_duration_ms: this.masterAudioDurationMs || null,
        slides: this.cues.map((c) => ({
          id: c.slideId || null,
          at_ms: c.atMs,
          step: Number.isFinite(c.step) ? c.step : 0,
          subtitle: c.subtitle || ''
        }))
      }
      const json = JSON.stringify(payload, null, 2)
      this.cueImportText = json
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(json).catch(() => {})
      }
    },
    updateFallbackDuration(seconds) {
      const n = parseFloat(seconds)
      if (!isFinite(n) || n <= 0) return
      this.fallbackSlideDurationMs = Math.round(n * 1000)
    },
    formatMs(ms) {
      if (!isFinite(ms)) return '0:00.0'
      const totalSec = ms / 1000
      const m = Math.floor(totalSec / 60)
      const s = (totalSec - m * 60).toFixed(1)
      return `${m}:${s.padStart(4, '0')}`
    },
    cueEndMs(idx) {
      const next = this.cues[idx + 1]
      if (next) return next.atMs
      if (this.masterAudioDurationMs) return this.masterAudioDurationMs
      return null
    },

    // ─── Input ──────────────────────────────────────────────────
    onKey(e) {
      const tag = (e.target && e.target.tagName) || ''
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable)) return

      // During arming, Esc always aborts (user's escape hatch). Other keys
      // only abort in the "prepare for external recording" flow — when we're
      // recording to a file, the user already committed via the browser
      // picker so stray keystrokes shouldn't nuke the countdown.
      if (this.recordingPhase === 'arming') {
        if (this.isWithinArmingGrace()) return
        if (e.key === 'Escape') {
          this.showAbortReason('keydown · Escape')
          this.cancelCountdown()
          return
        }
        if (!this.recordingToFile) {
          this.showAbortReason(`keydown · key="${e.key}" code="${e.code}"`)
          this.cancelCountdown()
        }
        return
      }

      if (e.key === 'Escape') {
        if (this.recordingPhase === 'active') {
          if (this.escConfirmPending) {
            this.exitRecording()
          } else {
            this.armEscConfirm()
          }
        }
        return
      }

      if (this.recordingPhase === 'active') {
        // Ignore all other keys while recording so the recorded video is clean.
        return
      }

      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault()
        this.togglePlay()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        this.nextSlide()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        this.prevSlide()
      }
    }
  }
}
</script>

<style scoped>
.video-player-page {
  position: fixed;
  inset: 0;
  background: #000;
  color: #fff;
  display: flex;
  flex-direction: column;
  outline: none;
}
/* ── Auto-load banner (?collection_id=…) ── */
.autoload-banner {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
  background: rgba(20, 20, 26, 0.9);
  border: 1px solid #2c5fa8;
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 13px;
  color: #e5e7eb;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
}
.autoload-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 8px #4ade80;
}
.autoload-text strong { color: #fff; font-weight: 500; }
.autoload-meta { color: #9ca3af; margin-left: 6px; font-family: monospace; font-size: 11px; }
.autoload-link {
  color: #93c5fd;
  text-decoration: none;
  font-size: 12px;
  border-left: 1px solid #374151;
  padding-left: 12px;
}
.autoload-link:hover { color: #bfdbfe; text-decoration: underline; }
.autoload-error {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
  background: rgba(127, 29, 29, 0.9);
  border: 1px solid #b91c1c;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 13px;
  color: #fecaca;
  font-family: monospace;
  max-width: 800px;
}
.video-player-page.cursor-hidden,
.video-player-page.cursor-hidden * {
  cursor: none !important;
}
.stage {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #000;
}
.stage-inner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow: hidden;
  /* width/height injected via :style — matches the rendered size so
     Region Capture crops to exactly the visible 16:9 area. */
}
.stage-content {
  position: absolute;
  top: 0;
  left: 0;
  /* width/height/transform injected via :style — fixed 1920x1080 logical
     surface, scaled down to match stage-inner's visual size. */
}
.slide-wrapper {
  width: 1920px;
  height: 1080px;
}

/* Countdown overlay */
.countdown-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.82);
  z-index: 50;
  color: #fff;
  text-align: center;
}
.countdown-label {
  font-size: 28px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.7;
  margin-bottom: 24px;
}
.countdown-number {
  font-size: 320px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.04em;
  animation: countdown-pop 1s ease-out;
}
@keyframes countdown-pop {
  0% { transform: scale(0.6); opacity: 0; }
  30% { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
.countdown-hint {
  margin-top: 32px;
  font-size: 18px;
  opacity: 0.6;
  letter-spacing: 0.02em;
}

/* ESC confirm toast */
.esc-confirm {
  position: absolute;
  top: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 14px;
  letter-spacing: 0.02em;
  z-index: 60;
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.esc-confirm kbd {
  background: rgba(255, 255, 255, 0.14);
  border-radius: 3px;
  padding: 1px 6px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  margin: 0 2px;
}

/* REC badge (visible during active recording) */
.rec-badge {
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 55;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.12em;
  border-radius: 4px;
  border: 1px solid rgba(220, 38, 38, 0.8);
}
.rec-dot {
  width: 10px;
  height: 10px;
  background: #DC2626;
  border-radius: 50%;
  animation: rec-pulse 1.2s ease-in-out infinite;
}
@keyframes rec-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

/* Saved toast (after a recording finalizes) */
.saved-toast {
  position: fixed;
  bottom: 28px;
  right: 28px;
  background: #0F172A;
  color: #fff;
  padding: 14px 18px;
  border-radius: 10px;
  border: 1px solid rgba(96, 165, 250, 0.35);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  z-index: 100;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.error-toast {
  position: fixed;
  bottom: 28px;
  right: 28px;
  background: rgba(220, 38, 38, 0.95);
  color: #fff;
  padding: 12px 18px;
  border-radius: 8px;
  z-index: 100;
  font-size: 13px;
}

/* Abort reason toast */
.abort-toast {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(220, 38, 38, 0.95);
  color: #fff;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  max-width: 80%;
  z-index: 70;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
}
.abort-toast-title {
  font-weight: 600;
  margin-bottom: 6px;
  opacity: 0.95;
}
.abort-toast-body {
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  opacity: 0.95;
  word-break: break-word;
}

/* Controls */
.controls {
  flex-shrink: 0;
  padding: 14px 20px 18px;
  background: rgba(10, 14, 22, 0.92);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  transition: opacity 250ms ease;
}
.progress {
  height: 4px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 12px;
}
.progress .bar {
  height: 100%;
  background: #60A5FA;
  transition: width 120ms linear;
}
.row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 14px;
}
.btn {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.14);
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}
.btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.16);
}
.btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.btn.primary {
  background: #2563EB;
  border-color: #2563EB;
  min-width: 110px;
}
.btn.primary:hover:not(:disabled) {
  background: #1D4ED8;
}
.btn.record {
  background: #DC2626;
  border-color: #DC2626;
  min-width: 110px;
  font-weight: 600;
}
.btn.record:hover:not(:disabled) {
  background: #B91C1C;
}
.slide-count {
  opacity: 0.7;
  margin-left: 8px;
}
.hint {
  margin-top: 10px;
  font-size: 12px;
  opacity: 0.55;
}
.hint kbd {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 3px;
  padding: 1px 6px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
}

/* Caption overlay — rendered inside the 1920x1080 stage so it scales with
   the slide automatically. */
.caption-layer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 80px;
  pointer-events: none;
  display: flex;
  justify-content: center;
  z-index: 40;
  padding: 0 80px;
}
.caption-bar {
  max-width: 1600px;
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  font-size: 44px;
  line-height: 1.3;
  font-weight: 500;
  padding: 18px 36px;
  border-radius: 8px;
  text-align: center;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
  font-family: Inter, system-ui, sans-serif;
}

/* Slide editor panel */
.toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.85;
  cursor: pointer;
  user-select: none;
}
.toggle input {
  margin: 0;
}
.spacer {
  flex: 1;
}
.editor {
  margin-top: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 14px 14px 4px;
  max-height: 48vh;
  overflow: auto;
}
.editor-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.editor-title {
  font-size: 13px;
  font-weight: 600;
}
.editor-subtitle {
  font-weight: 400;
  opacity: 0.6;
  margin-left: 6px;
}
.editor-actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}
.editor-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 10px;
}
.editor-empty {
  padding: 24px;
  text-align: center;
  opacity: 0.55;
  font-size: 13px;
}
.editor-section {
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.editor-section:last-child { border-bottom: none; }
.section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
  flex-wrap: wrap;
}
.section-note {
  opacity: 0.55;
  font-weight: 400;
  font-size: 12px;
}
.master-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.time-badge {
  font-family: ui-monospace, Menlo, monospace;
  background: rgba(96, 165, 250, 0.18);
  color: #bfdbfe;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}
.time-badge.subtle {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.55);
  font-weight: 400;
}
.import-details {
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 10px;
  font-size: 12px;
}
.import-details summary {
  cursor: pointer;
  opacity: 0.7;
  font-weight: 500;
}
.import-body {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.json-input {
  width: 100%;
  background: rgba(0, 0, 0, 0.4);
  color: #e5e7eb;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  padding: 8px 10px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
}
.import-error {
  color: #fca5a5;
  font-size: 12px;
}
.import-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}
.import-hint {
  opacity: 0.55;
  font-size: 11px;
}
.import-hint code {
  background: rgba(255, 255, 255, 0.08);
  padding: 1px 5px;
  border-radius: 3px;
  font-family: ui-monospace, Menlo, monospace;
}
.num.wider { width: 120px; }
.file-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}
.slide-id-input {
  width: 120px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  padding: 6px 10px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 13px;
}
.step-input {
  width: 64px;
  font-family: ui-monospace, Menlo, monospace;
}
.detected-ids {
  margin-top: 8px;
  font-size: 12px;
  opacity: 0.7;
}
.detected-ids code {
  background: rgba(96, 165, 250, 0.14);
  color: #bfdbfe;
  padding: 1px 6px;
  border-radius: 3px;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
}
.bundle-iframe {
  width: 1920px;
  height: 1080px;
  border: 0;
  display: block;
  background: #000;
}
.slide-row {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 10px 12px;
}
.slide-row-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 13px;
}
.slide-row-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.slide-row-body .field.wide {
  grid-column: 1 / -1;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}
.field label {
  opacity: 0.7;
  font-weight: 500;
}
.field input[type='file'] {
  color: inherit;
  font-size: 12px;
}
.field textarea {
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  padding: 8px 10px;
  font-family: Inter, system-ui, sans-serif;
  font-size: 13px;
  resize: vertical;
  min-height: 44px;
}
.field-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}
.inline-label {
  opacity: 0.6;
  font-size: 12px;
}
.num {
  width: 80px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
}
.file-status {
  opacity: 0.8;
  font-size: 12px;
}
.file-status.muted {
  opacity: 0.45;
}
.btn-sm {
  padding: 4px 10px;
  font-size: 12px;
}
.btn.danger {
  background: rgba(220, 38, 38, 0.18);
  border-color: rgba(220, 38, 38, 0.4);
}
.btn.danger:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.3);
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 400ms ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
}
.slide-slide-left-enter-active,
.slide-slide-left-leave-active {
  transition: transform 450ms ease, opacity 450ms ease;
}
.slide-slide-left-enter {
  transform: translateX(60px);
  opacity: 0;
}
.slide-slide-left-leave-to {
  transform: translateX(-60px);
  opacity: 0;
}
.slide-none-enter-active,
.slide-none-leave-active {
  transition: none;
}
</style>
